import { useEffect, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, PackageSearch, Search, X } from 'lucide-react'
import { formatNumber } from '@najah/core'
import { EmptyState, Input, Spinner, cn, useToast } from '@najah/ui'
import { api, type Paged, type StoreCategory, type StoreProduct } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useCart } from '../lib/cart'
import { ProductCard } from '../components/ProductCard'

export function HomeScreen({ onOpenProduct }: { onOpenProduct: (id: string) => void }) {
  const { customer } = useAuth()
  const cart = useCart()
  const toast = useToast()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [categoryId, setCategoryId] = useState<string | null>(null)

  // تأخير البحث حتى تتوقّف الكتابة — كل حرف طلب شبكة على اتصال ضعيف
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300)
    return () => clearTimeout(id)
  }, [search])

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<StoreCategory[]>('/categories'),
  })

  const { data, isPending } = useQuery({
    queryKey: ['products', { search: debounced, categoryId }],
    queryFn: () =>
      api<Paged<StoreProduct>>('/products', {
        query: { search: debounced || undefined, categoryId: categoryId ?? undefined, pageSize: 40 },
      }),
    placeholderData: keepPreviousData,
  })

  const favMutation = useMutation({
    mutationFn: (productId: string) => api<{ isFavorite: boolean }>(`/favorites/${productId}`, { method: 'POST' }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['favorites'] })
      toast.success(res.isFavorite ? 'أُضيف للمفضّلة' : 'أُزيل من المفضّلة')
    },
    onError: () => toast.error('سجّل الدخول لاستعمال المفضّلة'),
  })

  // التصنيفات الرئيسية فقط في الشريط — الشجرة كاملة تُربك على شاشة صغيرة
  const rootCategories = (categories ?? []).filter((c) => !c.parentId)

  return (
    <div className="space-y-4 p-4">
      <form onSubmit={(e) => { e.preventDefault(); setDebounced(search) }}>
        <Input
          placeholder="ابحث عن كتاب أو أداة…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          startIcon={<Search className="size-4" />}
          endIcon={
            search ? (
              <button
                type="button"
                onClick={() => { setSearch(''); setDebounced('') }}
                className="pointer-events-auto rounded p-0.5 text-fg-subtle"
                aria-label="مسح"
              >
                <X className="size-4" />
              </button>
            ) : undefined
          }
          className="h-12"
        />
      </form>

      {/* شريط التصنيفات — تمرير أفقي بلا شريط تمرير مرئي */}
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 scrollbar-none">
        <button
          type="button"
          onClick={() => setCategoryId(null)}
          className={cn(
            'shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors',
            categoryId === null
              ? 'border-primary bg-primary text-fg-on-primary'
              : 'border-border bg-surface text-fg-muted',
          )}
        >
          الكل
        </button>
        {rootCategories.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setCategoryId(c.id)}
            className={cn(
              'shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors',
              categoryId === c.id
                ? 'border-primary bg-primary text-fg-on-primary'
                : 'border-border bg-surface text-fg-muted',
            )}
          >
            {c.nameAr}
          </button>
        ))}
      </div>

      {data && (
        <p className="text-xs text-fg-subtle">
          {formatNumber(data.total)} منتج
          {debounced && <> · نتائج البحث عن «{debounced}»</>}
        </p>
      )}

      {isPending ? (
        <div className="grid place-items-center py-16">
          <Spinner className="size-7 text-primary" />
        </div>
      ) : data && data.items.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {data.items.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              onOpen={() => onOpenProduct(p.id)}
              onAdd={() => { cart.add(p); toast.success('أُضيف إلى السلة', p.nameAr) }}
              onToggleFavorite={customer ? () => favMutation.mutate(p.id) : undefined}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={debounced ? <PackageSearch className="size-10" /> : <BookOpen className="size-10" />}
          title={debounced ? 'لا توجد نتائج' : 'لا توجد منتجات'}
          description={debounced ? 'جرّب كلمة أخرى' : 'ستظهر المنتجات هنا قريباً'}
        />
      )}
    </div>
  )
}
